import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import LocationOn from "@mui/icons-material/LocationOn";
import MyLocation from "@mui/icons-material/MyLocation";
import Tooltip from "@mui/material/Tooltip";
import Loader from "../common/Loader";

export interface LocationValue {
  lat: number;
  lng: number;
  location_name: string;
}

interface Props {
  value?: LocationValue | null;
  onChange: (v: LocationValue) => void;
  apiKey: string;
  size?: "small" | "medium";
}

const SCRIPT_ID = "google-maps-sdk";

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.maps) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
};

export default function GoogleMapLocationPicker({
  value,
  onChange,
  apiKey,
  size = "small",
}: Props) {
  const [open, setOpen] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [map, setMap] = useState<google.maps.Map | null>(null);

  // FIX: Use useRef instead of useState for the Marker to prevent re-render loops
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const getAddress = (lat: number, lng: number): Promise<string> => {
    return new Promise((resolve) => {
      if (!geocoderRef.current) geocoderRef.current = new google.maps.Geocoder();
      geocoderRef.current.geocode({ location: { lat, lng } }, (res, status) => {
        if (status === "OK" && res && res[0]) resolve(res[0].formatted_address);
        else resolve("Unknown Location");
      });
    });
  };

  /** OPEN → load script → init map */
  useEffect(() => {
    let active = true;

    if (!open) {
      // Reset state when dialog closes to ensure fresh map/autocomplete on next open
      // This is crucial because Dialog unmounts its content, making old map/autocomplete refs invalid
      setMap(null);
      autocompleteRef.current = null;
      markerRef.current = null;
      return;
    }

    setMapLoading(true);

    loadGoogleMapsScript(apiKey)
      .then(() => {
        if (!active) return;
        geocoderRef.current = new google.maps.Geocoder();

        setTimeout(() => {
          if (!active || !mapRef.current) return;

          // Only create map if it doesn't exist yet (it should be null here since we reset on close)
          if (!map) {
            const center = value
              ? { lat: value.lat, lng: value.lng }
              : { lat: -37.8136, lng: 144.9631 }; // Melbourne, Australia

            const zoom = value ? 17 : 5;

            const m = new google.maps.Map(mapRef.current, {
              center,
              zoom,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
            });

            setMap(m);
          }
          setMapLoading(false);
        }, 300);
      })
      .catch(() => {
        if (active) setMapLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiKey]); // Removed 'value' and 'map' to prevent re-initialization logic conflicts

  /** When map loads or value changes → update marker dynamically */
  useEffect(() => {
    if (!map) return;

    // Logic 1: Handle "No Value"
    if (!value) {
      if (markerRef.current) markerRef.current.setMap(null);
      // Optional: Only reset view if the user hasn't interacted yet, 
      // but for now, we leave the view as is or reset to India if needed.
      return;
    }

    // Logic 2: Handle "New Value" (Update Marker)
    const pos = { lat: value.lat, lng: value.lng };

    // Clear old marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    const mk = new google.maps.Marker({
      position: pos,
      map,
      draggable: true,
      animation: google.maps.Animation.DROP
    });

    mk.addListener("dragend", async () => {
      const newPos = mk.getPosition();
      if (newPos) {
        const lat = newPos.lat();
        const lng = newPos.lng();
        const address = await getAddress(lat, lng);
        onChange({ lat, lng, location_name: address });
      }
    });

    markerRef.current = mk; // Update Ref (Does not trigger re-render)

    // Center Map
    map.panTo(pos);
    if (map.getZoom()! < 17) {
      map.setZoom(17);
    }

  }, [map, value, onChange]);

  /** Search Box and Map Click Listeners */
  // We combine these to ensure they are bound once when map is ready
  useEffect(() => {
    if (!map || !searchRef.current) return;

    // 1. Setup Autocomplete
    if (!autocompleteRef.current) {
      const autocomplete = new google.maps.places.Autocomplete(searchRef.current, {
        fields: ["geometry", "formatted_address", "name"],
      });
      autocompleteRef.current = autocomplete;

      autocomplete.addListener("place_changed", async () => {
        const place = autocomplete.getPlace();
        if (!place.geometry?.location) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || await getAddress(lat, lng);

        // Notify Parent
        onChange({ lat, lng, location_name: address });
      });
    }


    // 2. Setup Click Listener
    const clickListener = map.addListener("click", async (e: any) => {
      if (!e.latLng) return;

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const address = await getAddress(lat, lng);

      // Notify Parent
      onChange({ lat, lng, location_name: address });
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, onChange]); // removed 'marker' from dependencies

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setMapLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const address = await getAddress(lat, lng);
        
        onChange({ lat, lng, location_name: address });
        
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }
        setMapLoading(false);
      },
      (error) => {
        console.error("Error fetching location:", error);
        setMapLoading(false);
        alert("Unable to fetch your location. Please check your browser permissions.");
      },
      { enableHighAccuracy: true }
    );
  };

  return (
    <>
      <TextField
        size={size}
        fullWidth
        placeholder="Select location"
        value={value?.location_name || ""}
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen(true)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <LocationOn color="action" />
            </InputAdornment>
          ),
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>Select Location</DialogTitle>

        <DialogContent sx={{ height: 500, display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
          {/* Global styles for the Google Autocomplete dropdown */}
          <style>
            {`
              .pac-container {
                z-index: 9999 !important;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                border: 1px solid #e2e8f0;
                font-family: inherit;
                margin-top: 0px !important; 
                width: 100% !important;
                max-width: 855px !important; /* Approximately matches the md dialog content width */
                margin-left: 0 !important;
              }
              .pac-item {
                padding: 8px 12px;
                cursor: pointer;
                transition: background 0.2s;
              }
              .pac-item:hover {
                background-color: #f8fafc;
              }
              .pac-item-query {
                font-size: 0.9rem;
                color: #1e293b;
              }
              .pac-matched {
                color: #479577;
                font-weight: 600;
              }
              .pac-icon {
                display: none;
              }
            `}
          </style>

          {mapLoading && <Loader open />}

          <Box sx={{ mb: 1 }}>
            {/* Search field */}
            <input
              ref={searchRef}
              placeholder="Search for a location (e.g. city, street, or landmark)..."
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1.5px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "0.95rem",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
              }}
              onFocus={(e) => e.target.style.borderColor = "#479577"}
              onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
            />
          </Box>

          {/* Map Wrapper */}
          <Box sx={{ 
            position: "relative", 
            flexGrow: 1, 
            borderRadius: "8px", 
            overflow: "hidden",
            border: "1.5px solid #e2e8f0"
          }}>
            <div
              ref={mapRef}
              style={{
                width: "100%",
                height: "100%",
                background: "#f1f5f9",
              }}
            />
              <Tooltip title="Use current location">
                <Box
                  onClick={handleGetCurrentLocation}
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    left: 20,
                    backgroundColor: "#FFFFFF",
                    color: "#479577",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 2,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                      transform: "scale(1.05)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  <MyLocation sx={{ fontSize: "1.4rem" }} />
                </Box>
              </Tooltip>
            </Box>

            <Box sx={{ textAlign: "right", mt: 1.5 }}>
              <Button 
                onClick={() => setOpen(false)}
                sx={{ 
                  color: "#479577", 
                  fontWeight: 600,
                  '&:hover': { backgroundColor: 'rgba(71, 149, 119, 0.04)' }
                }}
              >
                Done
              </Button>
            </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}